import React from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Facebook, Twitter, Mail, MapPin, Heart } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const Footer = () => {
  const { t } = useTranslation();

  return (
    <footer className="bg-gray-900 text-white">
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="lg:col-span-1">
            <h3 className="text-2xl font-bold bg-gradient-to-r from-primary-500 to-success-500 bg-clip-text text-transparent mb-4">
              Snatched Latinas
            </h3>
            <p className="text-gray-300 mb-6 leading-relaxed">
              {t('footer.description')}
            </p>
            <div className="flex space-x-4">
              <a href="#" className="bg-primary-500 p-2 rounded-full hover:bg-success-500 transition-colors">
                <Instagram className="w-5 h-5" />
              </a>
              <a href="#" className="bg-primary-500 p-2 rounded-full hover:bg-success-500 transition-colors">
                <Facebook className="w-5 h-5" />
              </a>
              <a href="#" className="bg-primary-500 p-2 rounded-full hover:bg-success-500 transition-colors">
                <Twitter className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-bold mb-4">{t('footer.quickLinks')}</h4>
            <ul className="space-y-2">
              <li><Link to="/" className="text-gray-300 hover:text-primary-500 transition-colors">{t('footer.home')}</Link></li>
              <li><Link to="/products" className="text-gray-300 hover:text-primary-500 transition-colors">{t('footer.products')}</Link></li>
              <li><Link to="/about" className="text-gray-300 hover:text-primary-500 transition-colors">{t('footer.about')}</Link></li>
              <li><a href="/#testimonials" className="text-gray-300 hover:text-primary-500 transition-colors">{t('footer.testimonials')}</a></li>
              <li><Link to="/blog" className="text-gray-300 hover:text-primary-500 transition-colors">{t('footer.blog')}</Link></li>
              <li><Link to="/contact" className="text-gray-300 hover:text-primary-500 transition-colors">{t('footer.contact')}</Link></li>
            </ul>
          </div>

          {/* Categories */}
          <div>
            <h4 className="text-lg font-bold mb-4">{t('footer.categories')}</h4>
            <ul className="space-y-2">
              <li><Link to="/products?category=detox" className="text-gray-300 hover:text-primary-500 transition-colors">{t('footer.detox')}</Link></li>
              <li><Link to="/products?category=weight-loss" className="text-gray-300 hover:text-primary-500 transition-colors">{t('footer.weightLoss')}</Link></li>
              <li><Link to="/products?category=fiber" className="text-gray-300 hover:text-primary-500 transition-colors">{t('footer.fiber')}</Link></li>
              <li><Link to="/products?category=packs" className="text-gray-300 hover:text-primary-500 transition-colors">{t('footer.packs')}</Link></li>
              <li><Link to="/products?category=offers" className="text-gray-300 hover:text-primary-500 transition-colors">{t('footer.offers')}</Link></li>
              <li><Link to="/products?category=new" className="text-gray-300 hover:text-primary-500 transition-colors">{t('footer.newProducts')}</Link></li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-bold mb-4">{t('footer.contactUs')}</h4>
            <div className="space-y-3">
              <div className="flex items-center">
                <Mail className="w-5 h-5 text-primary-500 mr-3" />
                <span className="text-gray-300">{t('footer.email')}</span>
              </div>
              <div className="flex items-center">
              </div>
              <div className="flex items-center">
                <MapPin className="w-5 h-5 text-primary-500 mr-3" />
                <span className="text-gray-300">{t('footer.address')}</span>
              </div>
            </div>

            {/* Payment Methods */}
            <div className="mt-6">
              <h5 className="text-sm font-bold mb-2">{t('footer.paymentMethods')}</h5>
              <div className="flex space-x-2">
                <div className="bg-white rounded px-2 py-1">
                  <span className="text-xs font-bold text-gray-900">VISA</span>
                </div>
                <div className="bg-white rounded px-2 py-1">
                  <span className="text-xs font-bold text-gray-900">MC</span>
                </div>
                <div className="bg-white rounded px-2 py-1">
                  <span className="text-xs font-bold text-gray-900">PIX</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Footer */}
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex flex-col md:flex-row items-center space-y-2 md:space-y-0 md:space-x-6">
              <p className="text-gray-300 text-sm">
                {t('footer.copyright')}
              </p>
              <div className="flex items-center text-gray-300 text-sm">
                <span>{t('footer.madeWith')}</span>
                <Heart className="w-4 h-4 text-red-500 mx-1 fill-current" />
                <span>{t('footer.forLatinas')}</span>
              </div>
            </div>

            <div className="flex flex-wrap justify-center md:justify-end space-x-6 mt-4 md:mt-0">
              <Link to="/privacy" className="text-gray-300 hover:text-primary-500 text-sm transition-colors">
                {t('footer.privacy')}
              </Link>
              <Link to="/terms" className="text-gray-300 hover:text-primary-500 text-sm transition-colors">
                {t('footer.terms')}
              </Link>
              <Link to="/returns" className="text-gray-300 hover:text-primary-500 text-sm transition-colors">
                {t('footer.returns')}
              </Link>
              <Link to="/shipping" className="text-gray-300 hover:text-primary-500 text-sm transition-colors">
                {t('footer.shipping')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
